// filepath: apps/web/src/admin/tenants/queries.ts
//
// FR-TEN-6 / ISS-050: React Query hooks for the operator
// de-provisioning UI. Uses TanStack Query v5; the cache key is
// tenant-scoped (`['deprovision', tenantId]`) to prevent cross-tenant
// cache leaks (INV-1).
//
// The hooks call the platform admin endpoints (NOT the per-tenant
// endpoints) — the operator is cross-tenant by design. Authorization
// uses the platform operator token (PLATFORM_ADMIN_TOKEN), presented
// as `Authorization: Bearer <token>` by the shared fetch wrapper.
//
// Returns: typed responses that match the saga backend's
// `DeprovisionStatus` / `StartDeprovisionResponse` shapes. Types
// live in `./types.ts`.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  DeprovisionHookOptions,
  DeprovisionStatus,
  StartDeprovisionResponse,
} from "./types";

const POLL_INTERVAL_MS_DEFAULT = 2000;

async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token =
    (typeof window !== "undefined" &&
      (window as { __PLATFORM_ADMIN_TOKEN__?: string })
        .__PLATFORM_ADMIN_TOKEN__) ||
    "";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `admin request failed: ${res.status} ${res.statusText} ${text}`,
    );
  }
  return res.json() as Promise<T>;
}

/**
 * useDeprovisionStatus — polls the de-provisioning saga status for
 * the given tenant. Used by DeprovisionProgress to render the 5 step
 * chips and overall status badge. Polling stops automatically when
 * the saga reaches a terminal state (completed / failed / cancelled).
 */
export function useDeprovisionStatus(
  tenantId: string,
  options: DeprovisionHookOptions = {},
): UseQueryResult<DeprovisionStatus> {
  const { pollIntervalMs = POLL_INTERVAL_MS_DEFAULT, enabled = true } = options;
  return useQuery<DeprovisionStatus>({
    queryKey: ["deprovision", "status", tenantId],
    queryFn: () =>
      adminFetch<DeprovisionStatus>(
        `/admin/tenants/${tenantId}/deprovisioning`,
      ),
    enabled: Boolean(tenantId) && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling once the saga reaches a terminal state.
      if (!data) return pollIntervalMs;
      if (
        data.status === "completed" ||
        data.status === "failed" ||
        data.status === "cancelled"
      ) {
        return false;
      }
      return pollIntervalMs;
    },
    staleTime: 0,
  });
}

/**
 * useStartDeprovision — POSTs to start a new de-provisioning saga.
 * Returns the mutation; the caller passes the idempotency key.
 *
 * The idempotency key is REQUIRED for the backend to deduplicate
 * retries (FR-TEN-6 AC-7). The caller can pass a key (e.g. UUID) to
 * make the request idempotent on the network.
 */
export function useStartDeprovision(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    StartDeprovisionResponse,
    Error,
    { idempotencyKey: string; exportPath?: string }
  >({
    mutationFn: ({ idempotencyKey, exportPath }) =>
      adminFetch<StartDeprovisionResponse>(
        `/admin/tenants/${tenantId}/deprovisioning`,
        {
          method: "POST",
          headers: { "Idempotency-Key": idempotencyKey },
          body: JSON.stringify({ export_path: exportPath || "" }),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["deprovision", "status", tenantId],
      });
    },
  });
}

/**
 * useCancelDeprovision — POSTs to the cancel endpoint. Triggers
 * reverse-compensation in the saga runner (no-op for the irreversible
 * FR-TEN-6 deletes, but transitions the saga to `cancelled` / `failed`).
 */
export function useCancelDeprovision(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation<DeprovisionStatus, Error, void>({
    mutationFn: () =>
      adminFetch<DeprovisionStatus>(
        `/admin/tenants/${tenantId}/deprovisioning/cancel`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["deprovision", "status", tenantId],
      });
    },
  });
}

/**
 * useResumeDeprovision — POSTs to the resume endpoint. Used by the
 * progress UI when a saga has reached `failed` and the operator wants
 * to retry the failed step (already-completed deletes are NOT
 * re-executed per AC-6).
 */
export function useResumeDeprovision(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation<DeprovisionStatus, Error, void>({
    mutationFn: () =>
      adminFetch<DeprovisionStatus>(
        `/admin/tenants/${tenantId}/deprovisioning/resume`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["deprovision", "status", tenantId],
      });
    },
  });
}
